/**
 * IHT Risk Flags
 *
 * Highlights unusual, fragile, or review-sensitive parts of the IHT
 * calculation. Helps users see which parts of the estate need extra
 * attention before relying on the tax result.
 *
 * Uses existing calculation outputs — does not duplicate tax logic.
 */

import type { IHTCalculation, EstateAssets, EstateProfile } from '../../types';
import { resolveResidenceForConsumers } from './rnrbResidenceFacts';

export type IHTRiskSeverity = 'High' | 'Medium' | 'Low';

export interface IHTRiskFlag {
    id: string;
    title: string;
    severity: IHTRiskSeverity;
    why: string;
    nextStep: string;
}

const fmt = (n: number) => '£' + Math.round(n).toLocaleString('en-GB');

type Detector = (
    iht: IHTCalculation | null | undefined,
    assets: EstateAssets | undefined,
    profile: EstateProfile | undefined,
) => IHTRiskFlag | null;

// ── 1. Lifetime gifts review ────────────────────────────────────────

const lifetimeGiftsReview: Detector = (iht, assets) => {
    const chargeable = iht?.breakdown?.gifts_chargeable_total ?? 0;
    if (chargeable <= 0) return null;

    const gifts = assets?.lifetime_gifts ?? [];
    if (gifts.length === 0) return null;

    const nrbBase = 325_000;
    const combinedNrb = (iht?.breakdown?.nrb_available ?? 0) + chargeable;
    const usesSignificantNrb = chargeable > combinedNrb * 0.3;
    const hasExemptions = gifts.some(g => g.is_regular_from_income === true);
    const exceedsNrb = chargeable > combinedNrb;

    const severity: IHTRiskSeverity = exceedsNrb ? 'High' : usesSignificantNrb ? 'Medium' : 'Low';

    const reasons: string[] = [];
    reasons.push(`${fmt(chargeable)} in chargeable gifts within 7 years of death`);
    if (usesSignificantNrb) reasons.push(`This uses ${Math.round((chargeable / combinedNrb) * 100)}% of the available Nil Rate Band`);
    if (hasExemptions) reasons.push('An income exemption is relied on for some gifts');

    return {
        id: 'lifetime_gifts_review',
        title: 'Lifetime gifts require verification',
        severity,
        why: reasons.join('. ') + '.',
        nextStep: 'Verify gift dates, amounts, and exemptions with bank statements and recipient confirmation.',
    };
};

// ── 2. RNRB dependency ──────────────────────────────────────────────

const rnrbDependency: Detector = (iht, assets, profile) => {
    const rnrb = iht?.breakdown?.rnrb_available ?? 0;
    if (rnrb <= 0) return null;

    // The QRI from the shared Stage-1 resolution (never is_main_residence). No QRI (s.8FB, or unresolved) → no flag
    // here: the engine's own confidence flags report residence-history and nomination gaps.
    const residence = resolveResidenceForConsumers(assets?.properties, profile?.iht_details);
    const mainHome = residence.qri;
    if (!mainHome) return null;

    const passesTo = mainHome.deceased_share_passes_to;
    const hasDescAlloc = mainHome.beneficiary_allocations?.some(
        (a: any) => a.type === 'direct_descendant'
    );

    // If clearly confirmed, lower severity (a provisional legacy residence is not confirmed)
    const confirmed = passesTo === 'direct_descendants' || hasDescAlloc;
    const provisional = residence.provisional;

    return {
        id: 'rnrb_dependency',
        title: 'Tax result depends on RNRB',
        severity: provisional ? (confirmed ? 'Medium' : 'High') : confirmed ? 'Low' : 'High',
        why: `${fmt(rnrb)} Residence Nil Rate Band is included in the calculation. This requires the residence to pass to direct descendants (children, grandchildren, etc).`
            + (provisional ? ' The residence is used provisionally: it is not yet confirmed that they lived in it as their home.' : ''),
        nextStep: (provisional ? 'Confirm they lived in the property as their home. ' : '') + (confirmed
            ? 'Confirm the will or intestacy provisions direct the property to qualifying descendants.'
            : 'Confirm who inherits the residence — RNRB will be lost if it does not pass to direct descendants.'),
    };
};

// ── 3. RNRB taper ──────────────────────────────────────────────────

const rnrbTaper: Detector = (iht) => {
    const taperAmount = iht?.breakdown?.taper_amount ?? 0;
    if (taperAmount <= 0) return null;

    const rnrbBefore = iht?.breakdown?.rnrb_before_taper ?? 0;
    const rnrbAfter = iht?.breakdown?.rnrb_available ?? 0;
    const fullyTapered = rnrbAfter === 0 && rnrbBefore > 0;
    const gross = iht?.breakdown?.gross_value ?? 0;

    return {
        id: 'rnrb_taper',
        title: fullyTapered ? 'RNRB fully tapered away' : 'RNRB partially reduced by taper',
        severity: fullyTapered ? 'Medium' : 'Low',
        why: `Estate value of ${fmt(gross)} exceeds the £2M taper threshold. RNRB reduced by ${fmt(taperAmount)} (from ${fmt(rnrbBefore)} to ${fmt(rnrbAfter)}).`,
        nextStep: 'Verify the gross estate value is accurate — deductible debts or excluded assets may bring it below £2M and restore the RNRB.',
    };
};

// ── 4. Spouse transfer evidence ─────────────────────────────────────

const spouseTransferEvidence: Detector = (iht, _assets, profile) => {
    const tnrb = iht?.breakdown?.tnrb_detail;
    if (!tnrb) return null;

    const nrbTransferred = tnrb.nrb_transferred_amount ?? 0;
    const rnrbTransferred = tnrb.rnrb_transferred_amount ?? 0;
    if (nrbTransferred + rnrbTransferred <= 0) return null;

    const sp = profile?.iht_details?.predeceased_spouse;
    const confidence = sp?.confidence ?? 'unknown';

    // TNRBConfidence union is 'confirmed' | 'possible' | 'none'.
    // Previously compared against 'estimated' — an impossible value —
    // which meant the 'Medium' branch was dead code and any non-'confirmed'
    // confidence collapsed to 'High'.
    const severity: IHTRiskSeverity = confidence === 'confirmed' ? 'Low'
        : confidence === 'possible' ? 'Medium' : 'High';

    const total = nrbTransferred + rnrbTransferred;

    return {
        id: 'spouse_transfer_evidence',
        title: 'Transferred allowances require evidence',
        severity,
        why: `${fmt(total)} in transferred allowances from a predeceased spouse (${fmt(nrbTransferred)} NRB, ${fmt(rnrbTransferred)} RNRB). Confidence: ${confidence}.`,
        nextStep: 'Obtain the first spouse\'s death certificate, grant of probate, and IHT return to support the HMRC claim (form IHT402).',
    };
};

// ── 5. APR/BPR evidence ─────────────────────────────────────────────

const aprBprEvidence: Detector = (iht, assets) => {
    const bpr = iht?.breakdown?.bpr_relief_total ?? 0;
    const apr = iht?.breakdown?.apr_relief_total ?? 0;
    if (bpr + apr <= 0) return null;

    const unconfirmedBpr = (assets?.bpr_assets ?? []).filter(a => a.qualifies !== true && (Number(a.estimated_value) || 0) > 0);
    const unconfirmedApr = (assets?.apr_assets ?? []).filter(a => a.qualifies !== true && (Number(a.estimated_value) || 0) > 0);
    const hasUnconfirmed = unconfirmedBpr.length + unconfirmedApr.length > 0;

    const parts: string[] = [];
    if (bpr > 0) parts.push(`BPR ${fmt(bpr)}`);
    if (apr > 0) parts.push(`APR ${fmt(apr)}`);

    return {
        id: 'apr_bpr_evidence',
        title: 'APR/BPR relief claimed — evidence required',
        severity: hasUnconfirmed ? 'High' : 'Medium',
        why: `${parts.join(' and ')} relied on in the calculation. HMRC frequently challenges these reliefs and requires evidence of qualifying use, ownership duration, and asset valuations.`,
        nextStep: hasUnconfirmed
            ? 'Confirm eligibility for all claiming assets — some are currently unconfirmed. Gather accounts, ownership records, and valuations.'
            : 'Prepare ownership history, business accounts, and professional valuations to support the relief claim.',
    };
};

// ── 6. Liquidity pressure ───────────────────────────────────────────

const liquidityPressure: Detector = (iht, assets) => {
    const tax = iht?.total_tax_due ?? 0;
    if (tax <= 0) return null;

    const liquid = (assets?.bank_accounts ?? []).reduce((s, a) => {
        const val = Number((a as any).estimated_value) || 0;
        const isJoint = (a as any).ownership === 'joint';
        return s + (isJoint ? val / 2 : val);
    }, 0);

    if (liquid >= tax) return null;

    const shortfall = tax - liquid;
    const severe = shortfall > tax * 0.5;

    return {
        id: 'liquidity_pressure',
        title: 'IHT bill exceeds available cash',
        severity: severe ? 'High' : 'Medium',
        why: `Tax due of ${fmt(tax)} but only ${fmt(liquid)} in liquid assets. Shortfall: ${fmt(shortfall)}.`,
        nextStep: 'Explore HMRC instalment option for property/business assets, bridging finance, or early asset sales.',
    };
};

// ── 7. Ownership / title review ─────────────────────────────────────

const ownershipReview: Detector = (_iht, assets) => {
    const props = assets?.properties ?? [];
    const tic = props.filter(p => p.ownership_type === 'tenants_in_common');
    const jointAssumed = props.filter(p =>
        p.ownership_type === 'joint_tenants' &&
        (p.deceased_share_percent === undefined || p.deceased_share_percent === null)
    );
    const unknownOwnership = [
        ...props.filter(p => !p.ownership_type || p.ownership_type === 'unknown'),
        ...(assets?.bank_accounts ?? []).filter(a => !(a as any).ownership),
    ];

    const issues: string[] = [];
    if (tic.length > 0) issues.push(`${tic.length} propert${tic.length === 1 ? 'y' : 'ies'} held as tenants in common`);
    if (jointAssumed.length > 0) issues.push(`${jointAssumed.length} joint propert${jointAssumed.length === 1 ? 'y' : 'ies'} with assumed share`);
    if (unknownOwnership.length > 0) issues.push(`${unknownOwnership.length} asset(s) with unknown ownership`);

    if (issues.length === 0) return null;

    return {
        id: 'ownership_review',
        title: 'Property or asset ownership needs verification',
        severity: unknownOwnership.length > 0 ? 'High' : 'Medium',
        why: issues.join('; ') + '. Incorrect ownership assumptions affect the estate value and IHT calculation.',
        nextStep: 'Check Land Registry title, account documentation, and any declarations of trust to confirm ownership type and shares.',
    };
};

// ── 8. Filing complexity ────────────────────────────────────────────

const filingComplexity: Detector = (iht) => {
    const tax = iht?.total_tax_due ?? 0;
    const gross = iht?.breakdown?.gross_value ?? 0;
    const gifts = iht?.breakdown?.gifts_chargeable_total ?? 0;
    const bpr = iht?.breakdown?.bpr_relief_total ?? 0;
    const apr = iht?.breakdown?.apr_relief_total ?? 0;
    const tnrb = iht?.breakdown?.tnrb_detail?.nrb_transferred_amount ?? 0;

    // IHT400 route likely
    const needsIht400 = tax > 0 || gross > 3_000_000;
    if (!needsIht400) return null;

    const schedules: string[] = [];
    if (gifts > 0) schedules.push('IHT403 (gifts)');
    if (bpr > 0 || apr > 0) schedules.push('IHT413 (BPR/APR)');
    if (tnrb > 0) schedules.push('IHT402 (TNRB)');

    if (schedules.length < 2) return null;

    return {
        id: 'filing_complexity',
        title: 'Complex IHT400 filing required',
        severity: schedules.length >= 3 ? 'High' : 'Medium',
        why: `Full IHT400 return needed with ${schedules.length} supplementary schedules: ${schedules.join(', ')}. Multiple schedules increase the risk of errors and HMRC queries.`,
        nextStep: 'Consider professional help with the IHT400 filing. Gather all supporting evidence before submission.',
    };
};

// ── 9. Zero-tax but non-simple ──────────────────────────────────────

const zeroTaxNonSimple: Detector = (iht) => {
    const tax = iht?.total_tax_due ?? 0;
    if (tax > 0) return null;

    const bd = iht?.breakdown;
    if (!bd) return null;

    const reliefFactors: string[] = [];
    if ((bd.spouse_exemption_total ?? 0) > 0) reliefFactors.push('spouse exemption');
    if ((bd.rnrb_available ?? 0) > 0) reliefFactors.push('RNRB');
    if ((bd.tnrb_detail?.nrb_transferred_amount ?? 0) > 0) reliefFactors.push('transferred NRB');
    if ((bd.tnrb_detail?.rnrb_transferred_amount ?? 0) > 0) reliefFactors.push('transferred RNRB');
    if ((bd.bpr_relief_total ?? 0) > 0) reliefFactors.push('BPR');
    if ((bd.apr_relief_total ?? 0) > 0) reliefFactors.push('APR');
    if ((bd.charity_exemption_total ?? 0) > 0) reliefFactors.push('charity exemption');

    // Only flag if multiple reliefs are producing the zero result
    if (reliefFactors.length < 2) return null;

    // WP1: trust property that may belong in the estate is not included (engine gap), so the result is incomplete and a
    // zero liability is not established. The relief dependency is still reported; only the conclusion is qualified.
    if (bd.trust_estate_inclusion_gap) {
        return {
            id: 'zero_tax_non_simple',
            title: 'Provisional zero IHT depends on multiple reliefs',
            severity: reliefFactors.length >= 3 ? 'Medium' : 'Low',
            why: `No IHT is calculated from the figures currently included, but this is provisional: trust property that may belong in the estate has not been included, and the result also depends on ${reliefFactors.length} factors: ${reliefFactors.join(', ')}. Including the trust property, or disallowing any one of these, could make tax payable.`,
            nextStep: 'Have a trust or inheritance tax specialist confirm whether the trust property belongs in the estate, and verify evidence for each relief claimed.',
        };
    }

    return {
        id: 'zero_tax_non_simple',
        title: 'Zero IHT depends on multiple reliefs',
        severity: reliefFactors.length >= 3 ? 'Medium' : 'Low',
        why: `No IHT is due, but this depends on ${reliefFactors.length} factors: ${reliefFactors.join(', ')}. If any one of these is disallowed, tax may become payable.`,
        nextStep: 'Verify evidence for each relief claimed. A challenge to any single relief could change the outcome.',
    };
};

// ── 10. Gift donor attribution unclear ─────────────────────────────

const giftDonorUnclear: Detector = (_iht, assets) => {
    const gifts = assets?.lifetime_gifts ?? [];
    const unsure = gifts.filter(g => g.donor_attribution === 'unsure' && g.amount > 0);
    if (unsure.length === 0) return null;

    return {
        id: 'gift_donor_unclear',
        title: 'Gift ownership unclear',
        severity: 'Medium',
        why: `${unsure.length} gift${unsure.length !== 1 ? 's have' : ' has'} unclear donor attribution. These are currently treated as 50% from the deceased. If the gifts were made entirely by the deceased, more NRB may be used.`,
        nextStep: 'Confirm whether each gift was made by the deceased, the surviving spouse, or jointly from shared funds.',
    };
};

// ── Assemble ────────────────────────────────────────────────────────

const ALL_DETECTORS: Detector[] = [
    lifetimeGiftsReview,
    rnrbDependency,
    rnrbTaper,
    spouseTransferEvidence,
    aprBprEvidence,
    liquidityPressure,
    ownershipReview,
    filingComplexity,
    zeroTaxNonSimple,
    giftDonorUnclear,
];

const SEVERITY_ORDER: IHTRiskSeverity[] = ['High', 'Medium', 'Low'];

export function assessIHTRiskFlags(
    ihtCalc: IHTCalculation | null | undefined,
    assets: EstateAssets | undefined,
    profile: EstateProfile | undefined,
): IHTRiskFlag[] {
    const flags: IHTRiskFlag[] = [];

    for (const detect of ALL_DETECTORS) {
        const flag = detect(ihtCalc, assets, profile);
        if (flag) flags.push(flag);
    }

    // Sort by severity (High first)
    flags.sort((a, b) =>
        SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity)
    );

    return flags;
}
